/**
 * <ChapterBadge> — a chapter chip (atom, L0.1).
 *
 * The maison replacement for the legacy `SchoolSubjectBadge` used on the live-report card: a small,
 * outlined pill = the subject's icon + the chapter's NAME (which may wrap to 2 lines). Sibling to
 * `<SubjectBadge>` (subject-only, icon + 4-char label) — here the border wraps BOTH icon and the
 * chapter text, so it composes the atoms directly rather than reusing `<SubjectBadge>`.
 *
 * Consumes the pure `UIChapter` type (DS atoms may import UITypes). Visual parity with legacy:
 * neutral border + grey label, fill = subject token color at ~7% alpha (`…11`).
 */
import React from "react"
import { type StyleProp, type ViewStyle } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { Row } from "../containers/Row"
import { ImageIcon } from "../icons/ImageIcon"
import { Text } from "../typography/Text"
import { type UIChapter } from "../../../ports/primary/ui/types"
import { subjectTint } from "../../utils/subjectTouch"

export type ChapterBadgeProps = {
    chapter: UIChapter
    /** Extra style — e.g. to add spacing when several chips wrap. */
    style?: StyleProp<ViewStyle>
    testID?: string
}

export function ChapterBadge(props: ChapterBadgeProps) {
    const { chapter, style, testID } = props
    const { name, subject } = chapter
    const { theme } = useUnistyles()
    const tintBg = subjectTint(theme.colors[subject.color])

    return (
        <Row gap="1" align="center" style={[styles.chip(tintBg), style]} testID={testID}>
            <ImageIcon uri={subject.iconUrl} size={14} />
            <Text size="text-sm" color="text-primary">
                {`${subject.name} • ${name}`}
            </Text>
        </Row>
    )
}

const styles = StyleSheet.create((theme) => ({
    chip: (backgroundColor: string) => ({
        alignSelf: "flex-start" as const,
        flexShrink: 1,
        borderWidth: 1,
        borderColor: theme.colors.border,
        backgroundColor,
        borderRadius: theme.radius.sm,
        paddingVertical: 3,
        paddingHorizontal: 5,
    }),
}))
